// Copyright 2026 AllSpice. All rights reserved.

export type ComponentInstance = {
  pageId: string;
  pageOrder: number;
  pageName: string;
  pageNameHash: string;
  componentId: string;
  reference: string;
};

export type NetInstance = {
  pageId: string;
  pageOrder: number;
  pageName: string;
  pageNameHash: string;
  netId: string;
  netName: string;
  wireIds: string[];
  dataSelector: string;
};

export type MultiPageComponentIndex = {
  loading: boolean;
  error: string | null;
  instances: ComponentInstance[];
  byReference: Map<string, ComponentInstance[]>;
  netInstances: NetInstance[];
  byNet: Map<string, NetInstance[]>;
};

export type MultiPageSearchState = {
  selectedReference: string | null;
  selectedCategory: 'Reference' | 'Net' | null;
  selectedIndex: number;
  searchQuery: string;
  openPageId: string | null;
};

export type MultiPageSearchResult = {
  category: 'Reference' | 'Net';
  key: string;
  description: string;
  pageId: string;
  pageOrder: number;
  pageName: string;
  pageNameHash: string;
  componentId: string;
  dataSelector: string;
};

type MatchMode = 'exact' | 'prefix' | 'contains';

export function createDefaultMultiPageSearchState(): MultiPageSearchState {
  return {
    selectedReference: null,
    selectedCategory: null,
    selectedIndex: 0,
    searchQuery: '',
    openPageId: null,
  };
}

export function createEmptyMultiPageComponentIndex(overrides: Partial<MultiPageComponentIndex> = {}): MultiPageComponentIndex {
  return {
    loading: false,
    error: null,
    instances: [],
    byReference: new Map(),
    netInstances: [],
    byNet: new Map(),
    ...overrides,
  };
}

export function buildMultiPageSearchResults(
  byReference: Map<string, ComponentInstance[]>,
  byNet: Map<string, NetInstance[]>,
): MultiPageSearchResult[] {
  const results: MultiPageSearchResult[] = [];

  for (const [reference, instances] of byReference.entries()) {
    const firstInstance = instances[0];
    if (!firstInstance) {
      continue;
    }

    results.push({
      category: 'Reference',
      key: reference,
      description: instances.length === 1 ? firstInstance.pageName : `${instances.length} instances`,
      pageId: firstInstance.pageId,
      pageOrder: firstInstance.pageOrder,
      pageName: firstInstance.pageName,
      pageNameHash: firstInstance.pageNameHash,
      componentId: firstInstance.componentId,
      dataSelector: `g[data-id='${CSS.escape(firstInstance.componentId)}']`,
    });
  }

  for (const [netName, instances] of byNet.entries()) {
    const firstInstance = instances[0];
    if (!firstInstance) {
      continue;
    }

    results.push({
      category: 'Net',
      key: netName,
      description: instances.length === 1 ? firstInstance.pageName : `${instances.length} instances`,
      pageId: firstInstance.pageId,
      pageOrder: firstInstance.pageOrder,
      pageName: firstInstance.pageName,
      pageNameHash: firstInstance.pageNameHash,
      componentId: '',
      dataSelector: firstInstance.dataSelector,
    });
  }

  return results;
}

function normalizeReference(value: string): string {
  return value.trim().toLowerCase();
}

function sortSearchResults(results: MultiPageSearchResult[]): MultiPageSearchResult[] {
  return Array.from(results).sort((left, right) => {
    // Sort by category first (Reference before Net)
    if (left.category !== right.category) {
      return left.category === 'Reference' ? -1 : 1;
    }

    const normalizedLeft = normalizeReference(left.key);
    const normalizedRight = normalizeReference(right.key);
    const normalizedComparison = normalizedLeft.localeCompare(normalizedRight, undefined, {numeric: true, sensitivity: 'base'});
    if (normalizedComparison !== 0) {
      return normalizedComparison;
    }

    const lengthComparison = left.key.length - right.key.length;
    if (lengthComparison !== 0) {
      return lengthComparison;
    }

    return left.key.localeCompare(right.key, undefined, {numeric: true, sensitivity: 'base'});
  });
}

function dedupeSearchResults(results: MultiPageSearchResult[]): MultiPageSearchResult[] {
  const seen = new Set<string>();
  const deduped: MultiPageSearchResult[] = [];

  for (const result of results) {
    const dedupeKey = `${result.category}::${normalizeReference(result.key)}`;
    if (seen.has(dedupeKey)) {
      continue;
    }

    seen.add(dedupeKey);
    deduped.push(result);
  }

  return deduped;
}

function matchesReference(key: string, normalizedQuery: string, mode: MatchMode): boolean {
  const normalizedKey = normalizeReference(key);

  switch (mode) {
    case 'exact':
      return normalizedKey === normalizedQuery;
    case 'prefix':
      return normalizedKey.startsWith(normalizedQuery);
    case 'contains':
      return normalizedKey.includes(normalizedQuery);
  }
}

export function filterMultiPageSearchResults(
  results: MultiPageSearchResult[],
  query: string,
  limit = 50,
): MultiPageSearchResult[] {
  const normalizedQuery = normalizeReference(query);
  const sortedResults = dedupeSearchResults(sortSearchResults(results));

  if (!normalizedQuery) {
    return sortedResults.slice(0, limit);
  }

  const exactMatches = sortedResults.filter((result) => matchesReference(result.key, normalizedQuery, 'exact'));
  const prefixMatches = sortedResults.filter((result) => {
    return matchesReference(result.key, normalizedQuery, 'prefix') &&
      !matchesReference(result.key, normalizedQuery, 'exact');
  });

  if (exactMatches.length > 0 || prefixMatches.length > 0) {
    return [...exactMatches, ...prefixMatches].slice(0, limit);
  }

  return sortedResults.filter((result) => matchesReference(result.key, normalizedQuery, 'contains')).slice(0, limit);
}
